import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, Remove, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import type { Product } from '../../types';
import CustomerPicker, { PickedCustomer } from '../../components/commercial/CustomerPicker';

/** Regalo/muestra o pérdida: baja de stock que no es venta, con su costo contado en el lote. */
const MobileStockOutPage: React.FC = () => {
  const navigate = useNavigate();
  const [reason, setReason] = useState<'GIFT' | 'LOSS'>('GIFT');
  const [products, setProducts] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [qty, setQty] = useState(1);
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.getProducts({ page: 1, pageSize: 100 }).then(r => setProducts((r.products || []).filter(p => p.isActive !== false && p.trackStock))).catch(() => setProducts([]));
  }, []);

  const submit = async () => {
    if (!product) { setError('Elegí el producto'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.stockOut({ productId: product.id, quantity: qty, reason, customerId: customer?.id, notes: notes || undefined });
      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ pt: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" fontWeight={800}>{reason === 'GIFT' ? 'Regalo registrado' : 'Pérdida registrada'}</Typography>
        <Typography variant="h6">{qty} × {product?.name}</Typography>
        <Button variant="contained" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => { setDone(false); setProduct(null); setQty(1); setNotes(''); }}>Otra</Button>
        <Button variant="outlined" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => navigate('/m')}>Volver al inicio</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup exclusive fullWidth value={reason} onChange={(_, v) => v && setReason(v)}>
        <ToggleButton value="GIFT" sx={{ minHeight: 52, fontSize: 16 }}>Regalo / muestra</ToggleButton>
        <ToggleButton value="LOSS" sx={{ minHeight: 52, fontSize: 16 }}>Pérdida / rotura</ToggleButton>
      </ToggleButtonGroup>
      <Stack spacing={1}>
        {products.map(p => (
          <Card key={p.id} variant="outlined" sx={{ borderColor: product?.id === p.id ? 'primary.main' : undefined, borderWidth: product?.id === p.id ? 2 : 1 }}>
            <CardActionArea onClick={() => setProduct(p)}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>{p.name}</Typography>
                <Typography variant="body2" color="text.secondary">stock {p.currentStock}</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => setQty(q => Math.max(1, q - 1))} sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} aria-label="Menos"><Remove /></IconButton>
        <TextField type="number" value={qty} onChange={e => setQty(Number(e.target.value) || 0)}
          inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontSize: 28, fontWeight: 800 } }} sx={{ flex: 1 }} />
        <IconButton onClick={() => setQty(q => q + 1)} sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} aria-label="Más"><Add /></IconButton>
      </Stack>
      {reason === 'GIFT' && <CustomerPicker value={customer} onChange={setCustomer} label="Para quién (opcional)" />}
      <TextField label="Nota (opcional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder={reason === 'GIFT' ? 'Ej: muestras para la vidriera' : 'Ej: pomos rotos en el envío'} />
      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" size="large" color={reason === 'GIFT' ? 'secondary' : 'error'} onClick={submit} disabled={saving || !product}
        sx={{ minHeight: 60, fontSize: 20, fontWeight: 800 }}>
        {saving ? 'Guardando…' : `Registrar ${qty} ${reason === 'GIFT' ? 'de regalo' : 'perdidas'}`}
      </Button>
    </Stack>
  );
};

export default MobileStockOutPage;
